/**
 * Three profile slots: create one by typing a name, pick one to play, or delete
 * one by typing its name back.
 */
import Phaser from 'phaser';
import { audioOf } from '../systems/audioRegistry';
import { pressButton, styleButton } from './ui/menuButton';
import { setDevState } from '../systems/devQuery';
import { drawVerticalGradient, MENU_GRADIENT } from '../art/backdrop';
import { PROFILE_SLOTS } from '../systems/SaveManager';
import { ANIMALS } from '../data/animals';
import type { ProfileSlot, SaveManager } from '../systems/SaveManager';
import { FONT_FAMILY, GAME_HEIGHT, GAME_WIDTH, IS_PORTRAIT, PALETTE } from '../types';
import { IconButton } from './ui/IconButton';
import { NameEntry } from './profile/NameEntry';
import { setTouchTyping } from '../systems/touchKeyboard';

const CARD_WIDTH = 340;
const CARD_HEIGHT = 300;
const CARD_Y = 300;

interface CardAction {
  readonly slot: number;
  readonly kind: 'play' | 'create' | 'delete';
  readonly label: string;
}

export class ProfileSelect extends Phaser.Scene {
  private save!: SaveManager;
  private nameEntry: NameEntry | undefined;
  private actions: CardAction[] = [];
  private buttons: Phaser.GameObjects.Text[] = [];
  private selected = 0;

  constructor() {
    super('ProfileSelect');
  }

  create(): void {
    setDevState({ scene: 'ProfileSelect' });
    // A NameEntry left over from a previous visit is already destroyed.
    this.nameEntry = undefined;
    this.save = this.registry.get('save') as SaveManager;
    this.cameras.main.setBackgroundColor(PALETTE.sky);
    this.drawBackdrop();
    this.build();
  }

  private build(): void {
    this.children.removeAll();
    this.actions = [];
    this.buttons = [];
    this.selected = 0;

    this.drawBackdrop();

    this.add
      .text(GAME_WIDTH / 2, 92, 'Who is climbing?', {
        fontFamily: FONT_FAMILY,
        fontSize: '58px',
        color: PALETTE.snow,
        stroke: PALETTE.ink,
        strokeThickness: 8,
      })
      .setOrigin(0.5);
    // A back button on every screen size: Esc alone left a mouse with no way out.
    new IconButton(
      this,
      56,
      IS_PORTRAIT ? 92 : 56,
      'back',
      () => this.scene.start('Title'),
      IS_PORTRAIT ? 60 : 52,
    );

    for (let slot = 0; slot < PROFILE_SLOTS; slot += 1) {
      this.drawCard(slot, this.save.profiles()[slot] ?? null);
    }

    this.add
      .text(
        GAME_WIDTH / 2,
        GAME_HEIGHT - 54,
        IS_PORTRAIT
          ? 'Tap Play, or New player to start'
          : 'Arrow keys and Enter      Esc for the title',
        {
          fontFamily: FONT_FAMILY,
          fontSize: '20px',
          color: PALETTE.ice,
        },
      )
      .setOrigin(0.5);

    if (!this.save.storageAvailable) {
      this.add
        .text(
          GAME_WIDTH / 2,
          GAME_HEIGHT - 86,
          'This browser is not saving progress, so this visit will not be remembered.',
          { fontFamily: FONT_FAMILY, fontSize: '18px', color: PALETTE.sunset },
        )
        .setOrigin(0.5);
    }

    this.select(0);
    this.installKeys();
  }

  private drawCard(slot: number, profile: ProfileSlot | null): void {
    // Wide stage: three cards side by side. Tall stage: stacked, full width.
    const centerX = IS_PORTRAIT ? GAME_WIDTH / 2 : GAME_WIDTH / 2 + (slot - 1) * (CARD_WIDTH + 36);
    const cardTop = IS_PORTRAIT ? 210 + slot * (CARD_HEIGHT + 40) : CARD_Y;
    const cardW = IS_PORTRAIT ? GAME_WIDTH - 80 : CARD_WIDTH;

    const card = this.add.graphics();
    card.fillStyle(profile === null ? 0x16233a : 0x1d3557, 0.95);
    card.fillRoundedRect(centerX - cardW / 2, cardTop, cardW, CARD_HEIGHT, 24);
    card.lineStyle(5, profile === null ? 0x44546b : 0x8ecae6, 1);
    card.strokeRoundedRect(centerX - cardW / 2, cardTop, cardW, CARD_HEIGHT, 24);

    if (profile === null) {
      this.add
        .text(centerX, cardTop + 96, 'Empty', {
          fontFamily: FONT_FAMILY,
          fontSize: '34px',
          color: PALETTE.ice,
        })
        .setOrigin(0.5);
      this.addButton(centerX, cardTop + 196, 'New player', {
        slot,
        kind: 'create',
        label: 'New player',
      });
      return;
    }

    this.add
      .text(centerX, cardTop + 54, profile.name, {
        fontFamily: FONT_FAMILY,
        fontSize: '38px',
        color: PALETTE.snow,
      })
      .setOrigin(0.5);

    const completed = Object.values(profile.levels).filter((r) => r.completed).length;
    const stars = Object.values(profile.levels).reduce((sum, r) => sum + r.bestStars, 0);
    const lines = [
      `${completed} level${completed === 1 ? '' : 's'} done`,
      `${stars} star${stars === 1 ? '' : 's'}`,
      `${profile.unlockedAnimals.length} of ${ANIMALS.length} animals`,
    ];
    lines.forEach((line, index) => {
      this.add
        .text(centerX, cardTop + 110 + index * 32, line, {
          fontFamily: FONT_FAMILY,
          fontSize: '22px',
          color: PALETTE.ice,
        })
        .setOrigin(0.5);
    });

    this.addButton(centerX - 70, cardTop + 240, 'Play', { slot, kind: 'play', label: 'Play' });
    this.addButton(centerX + 76, cardTop + 240, 'Delete', {
      slot,
      kind: 'delete',
      label: 'Delete',
    });
  }

  private addButton(x: number, y: number, label: string, action: CardAction): void {
    const index = this.actions.length;
    this.actions.push(action);

    const button = this.add
      .text(x, y, label, {
        fontFamily: FONT_FAMILY,
        fontSize: '24px',
        color: PALETTE.snow,
        backgroundColor: '#1b2a41',
        padding: { x: 14, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });

    button.on('pointerover', () => this.select(index));
    button.on('pointerup', () => pressButton(button, () => this.run(index)));
    this.buttons.push(button);
  }

  private installKeys(): void {
    this.input.keyboard?.removeAllListeners('keydown');
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.nameEntry?.handleKey(event) === true) return;

      if (event.key === 'Escape') {
        this.scene.start('Title');
        return;
      }
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        this.select((this.selected + this.actions.length - 1) % this.actions.length);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        this.select((this.selected + 1) % this.actions.length);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const index = this.selected;
        const button = this.buttons[index];
        if (button !== undefined) pressButton(button, () => this.run(index));
      }
    });
  }

  private select(index: number): void {
    if (this.buttons.length === 0) return;
    this.selected = index;
    this.buttons.forEach((button, i) => styleButton(button, i === index));
  }

  private run(index: number): void {
    const action = this.actions[index];
    if (action === undefined) return;

    if (action.kind === 'play') {
      this.save.selectSlot(action.slot);
      this.applyProfileSettings();
      this.scene.start('LevelMap');
      return;
    }

    if (action.kind === 'create') {
      this.openNameEntry({
        title: 'What is your name?',
        hint: 'Type it with the keyboard - letters and spaces, up to 12.',
        confirmLabel: 'start',
        onConfirm: (name) => {
          this.save.addProfile(action.slot, name);
          this.applyProfileSettings();
          // A new player says their grade and picks helpers before the first climb.
          this.scene.start('Grade');
        },
      });
      return;
    }

    const profile = this.save.profiles()[action.slot];
    if (profile === null || profile === undefined) return;
    this.openNameEntry({
      title: `Delete ${profile.name}?`,
      hint: `This cannot be undone. Type ${profile.name} to confirm.`,
      mustMatch: profile.name,
      confirmLabel: 'delete',
      onConfirm: () => {
        this.save.deleteProfile(action.slot);
        this.build();
      },
    });
  }

  private openNameEntry(options: {
    title: string;
    hint: string;
    confirmLabel: string;
    mustMatch?: string;
    onConfirm: (name: string) => void;
  }): void {
    this.nameEntry?.destroy();
    this.nameEntry = new NameEntry(this, {
      title: options.title,
      hint: options.hint,
      confirmLabel: options.confirmLabel,
      ...(options.mustMatch === undefined ? {} : { mustMatch: options.mustMatch }),
      onConfirm: (name) => {
        this.nameEntry = undefined;
        setTouchTyping(false);
        options.onConfirm(name);
      },
      onCancel: () => {
        this.nameEntry = undefined;
        setTouchTyping(false);
      },
    });
    this.nameEntry.show();
    // Phones: typing a name needs the phone keyboard; a tap opens it.
    setTouchTyping(true);
  }

  /** Mirror the profile's settings into the registry the Game scene reads. */
  private applyProfileSettings(): void {
    const settings = this.save.settings();
    this.registry.set('reducedMotion', settings.reducedMotion);
    this.sound.mute = settings.sfxMuted && settings.musicMuted;
    audioOf(this)?.applySettings(settings);
  }

  private drawBackdrop(): void {
    drawVerticalGradient(this, MENU_GRADIENT);
  }
}
